import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
	PEOPLE_SEARCH_CATEGORIES,
	isCategoryActive,
} from "@/types/peopleSearch";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";
import { renderCategoryPanel } from "./CategoryPanel";

export function MobileFiltersSheet() {
	const { t } = useTranslation();
	const { draft, dirtyCategories, apply, reset } = usePeopleSearch();
	const [open, setOpen] = useState(false);

	const activeCount = PEOPLE_SEARCH_CATEGORIES.filter(category =>
		isCategoryActive(draft, category)).length;

	const applyAndClose = () => {
		apply();
		setOpen(false);
	};

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger asChild>
				<Button variant="outline" size="sm" className="relative shrink-0">
					<SlidersHorizontal className="h-4 w-4 mr-2" />
					{t("peopleSearch.mobile.filters", "Filters")}
					{activeCount > 0 && (
						<Badge variant="secondary" className="ml-2 text-xs">
							{activeCount}
						</Badge>
					)}
					{dirtyCategories.length > 0 && (
						<span
							className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500"
							aria-label={t("peopleSearch.rail.unapplied", "Unapplied changes")}
						/>
					)}
				</Button>
			</SheetTrigger>
			<SheetContent side="bottom" className="h-[85dvh] flex flex-col p-0">
				<SheetHeader className="px-4 pt-4 pb-2 text-left shrink-0">
					<SheetTitle>{t("peopleSearch.mobile.filters", "Filters")}</SheetTitle>
					<SheetDescription className="sr-only">
						{t("peopleSearch.description", "Build a precise search across your whole candidate database")}
					</SheetDescription>
				</SheetHeader>
				<div className="flex-1 min-h-0 overflow-y-auto px-4">
					<Accordion type="single" collapsible>
						{PEOPLE_SEARCH_CATEGORIES.map(category => {
							const active = isCategoryActive(draft, category);
							const dirty = dirtyCategories.includes(category);
							return (
								<AccordionItem key={category} value={category}>
									<AccordionTrigger className="text-sm">
										<span className="flex items-center gap-2">
											{t(`peopleSearch.categories.${category}`, category)}
											{(active || dirty) && (
												<span
													className={cn(
														"h-2 w-2 rounded-full",
														dirty ? "bg-amber-500" : "bg-digger-blue"
													)}
													aria-label={dirty
														? t("peopleSearch.rail.unapplied", "Unapplied changes")
														: t("peopleSearch.rail.active", "Active filters")}
												/>
											)}
										</span>
									</AccordionTrigger>
									<AccordionContent>
										{renderCategoryPanel(category)}
									</AccordionContent>
								</AccordionItem>
							);
						})}
					</Accordion>
				</div>
				<div className="border-t px-4 py-3 flex items-center justify-between gap-2 shrink-0">
					<Button
						variant="ghost"
						size="sm"
						onClick={reset}
						className="text-muted-foreground"
					>
						<RotateCcw className="h-4 w-4 mr-2" />
						{t("peopleSearch.reset", "Reset")}
					</Button>
					<Button size="sm" onClick={applyAndClose}>
						{t("peopleSearch.mobile.done", "Apply filters")}
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	);
}
